import classNames from "classnames";
import { camelize } from "humps";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { ProfileCustomValuesList } from "PFApp/components/custom_values_list";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { RouterPrompt } from "PFComponents/router_prompt/router_prompt";
import { isRankable } from "PFCore/helpers/custom_type";
import { ownProfileCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useConfirm } from "PFCore/helpers/use_confirm";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { CustomType, Profile, PureCustomValueData } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./expertise_field.module.scss";
import { PageSection } from "./page_section";

const VISIBLE_VALUES_LIMIT = 6;

type ExpertiseFieldProps = {
  customType: CustomType;
  profile: Profile;
  recalcTargets?: VoidFunction;
  onEditModeChange: VoidFunction;
  onProfileUpdate: (fields: { [key: string]: any }) => Promise<unknown>;
  isMe?: boolean;
  isEditMode?: boolean;
  isLoading?: boolean;
  className?: string;
};

export const ExpertiseField = ({
  isEditMode,
  onEditModeChange,
  customType,
  profile,
  isMe,
  isLoading,
  recalcTargets,
  onProfileUpdate,
  className
}: ExpertiseFieldProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation(["profiles", "translation"]);
  const [updatedValue, setUpdatedValue] = useState<PureCustomValueData[] | null>(null);
  const { invalidateForType: invalidateCustomValuesForType } = useCustomValuesInvalidate();

  const isEditPermitted = useMemo(() => {
    if (!currentProfile.permissionsGroup || !isMe) {
      return false;
    }
    return ownProfileCustomTypesAccessLevels(currentProfile)[customType.id] === "rw";
  }, [currentProfile, customType, isMe]);

  const handleProfileUpdate = () => {
    onProfileUpdate(updatedValue !== null ? { [customType?.name]: updatedValue } : {}).then(() => {
      setUpdatedValue(null);
      showGrowl({
        message: t("profiles:show.parts.customTypeUpdated", { customType: customType.display_as }),
        kind: "success"
      });
      onEditModeChange();
      invalidateCustomValuesForType(customType.id);
    });
  };

  const isChanged = updatedValue !== null;
  const confirmNavigation = useConfirm(isChanged);

  const values = updatedValue || profile?.[camelize(customType?.name)] || [];
  const title = customType.display_as || customType.name;

  const handleEditModeChange = () => {
    if (isEditMode && isChanged) {
      if (!confirmNavigation()) {
        return;
      }
    }
    setUpdatedValue(null);
    onEditModeChange();
  };

  return (
    <PageSection
      className={classNames(className, isEditMode ? css.visible : css.hidden)}
      key={customType.id}
      title={title || t("profiles:show.parts.unknown")}
      scrollKey={`cf_${customType.name}`}
      action={
        isEditPermitted && (
          <ActionIcon
            title={
              isEditMode
                ? t("profiles:show.parts.sectionAdditional.closeEditMode")
                : t("profiles:show.parts.sectionAdditional.openEditMode", { name: title })
            }
            onClick={handleEditModeChange}
            name={isEditMode ? "cross" : "edit"}
            size="sm"
          />
        )
      }
    >
      {isEditMode ? (
        <div>
          <RouterPrompt when={isEditMode && isChanged} />
          <CustomValuesEditField
            customType={customType}
            qaIdPrefix="profile-expertise"
            values={values}
            multi={customType.kind === "multiple"}
            adminPage={false}
            profile={profile}
            perPage={250}
            handleChange={(res) => setUpdatedValue(res)}
            classes={{ root: css.customValueField }}
            letClear
          />
          <div className={css.buttonWrapper}>
            <Button onClick={handleProfileUpdate} disabled={isLoading}>
              {t("translation:update")}
            </Button>
          </div>
        </div>
      ) : (
        <ProfileCustomValuesList
          customFieldName={customType.name}
          profileId={profile.id}
          customValues={values}
          limit={VISIBLE_VALUES_LIMIT}
          showLeftIcon={isRankable(customType)}
          onMoreLessButtonClick={recalcTargets}
          canEdit
        />
      )}
    </PageSection>
  );
};
